<template>
  <div v-if="mostrar" class="modal-overlay">
    <div class="modal-container">
      <!-- Cabeçalho -->
      <div class="modal-header">
        <h6>Realizar Pós-venda</h6>
        <i class="bi bi-x-circle close-icon" @click="fecharModal"></i>
      </div>

      <!-- Conteúdo do Modal -->
      <div class="modal-content">
        <!-- Enquanto isLoading for true, exibe somente o LoadingSpinner -->
        <template v-if="isLoading">
          <LoadingSpinner />
        </template>
        <!-- Quando isLoading for false, exibe o conteúdo do modal -->
        <template v-else>
          <!-- Exibe a descrição atual da pós-venda -->
          <label>
            Última ação realizada em {{ utils.formatarData(posvenda.data_contato) }} por {{ posvenda.nome_usuario }}
          </label>
          <h6>{{ descricaoFormatada }}</h6>
          <p></p>

          <!-- Dados do Aluno -->
          <div class="row mb-2">
            <div class="col">
              <label>Aluno</label>
              <input type="text" :value="alunoInfo.nome" class="form-control" disabled />
            </div>
            <div class="col">
              <label>Celular</label>
              <input type="text" :value="alunoInfo.celular" class="form-control" disabled />
            </div>
            <div class="col">
              <label>Email</label>
              <input type="text" :value="alunoInfo.email" class="form-control" disabled />
            </div>
          </div>

          <!-- Dados do Responsável -->
          <div class="row mb-2">
            <div class="col">
              <label>Responsável</label>
              <input type="text" :value="responsavelInfo.nome" class="form-control" disabled />
            </div>
            <div class="col">
              <label>Celular</label>
              <input type="text" :value="responsavelInfo.celular" class="form-control" disabled />
            </div>
            <div class="col">
              <label>Email</label>
              <input type="text" :value="responsavelInfo.email" class="form-control" disabled />
            </div>
          </div>

          <!-- Tabela de Histórico: exibe somente se houver registros -->
          <div class="row mb-2" v-if="historico.length > 0">
            <div class="col">
              <label>Histórico</label>
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Contato</th>
                    <th>Descrição</th>
                    <th>Agendamento</th>
                    <th>Por</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in historico" :key="item.id">
                    <td>{{ utils.formatarData(item.data_contato) }}</td>
                    <td>{{ formatarDatasNoTexto(item.descricao) }}</td>
                    <td>{{ utils.formatarData(item.proximo_contato) }}</td>
                    <td>{{ item.nome_usuario }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Campo: Descrição para atualizar o registro de posvenda -->
          <div class="row mt-2">
            <div class="col">
              <label for="descricao">Descrição <span class="required">*</span></label>
              <textarea
                id="descricao"
                v-model="form.descricao"
                class="form-control"
                rows="3"
                required
              ></textarea>
            </div>
          </div>

          <!-- Campo: Agendar Próximo Contato (não obrigatório) -->
          <div class="row mt-2">
            <div class="col">
              <label for="proximo_contato">Agendar Próximo Contato?</label>
              <input
                type="date"
                id="proximo_contato"
                v-model="form.proximo_contato"
                class="form-control"
              />
            </div>
          </div>

          <!-- Botão Salvar -->
          <div class="row mt-3">
            <div class="col text-end">
              <button
                :class="['btn', isSubmitting ? 'btn-success' : 'btn-primary']"
                @click="salvar"
                :disabled="!formIsValid || isSubmitting"
              >
                {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, getCurrentInstance } from "vue";
import axios from "@/services/axios";
import globalUtils from "@/utils/globalUtils";

const instance = getCurrentInstance(); // Capture a instância no início
const isLoading = ref(true);
const isSubmitting = ref(false);


const props = defineProps({
  mostrar: { type: Boolean, default: false },
  // Registro de posvenda que acionou o modal (já com aluno_id e responsavel_id)
  posvenda: { type: Object, default: () => ({}) }
});
const emit = defineEmits(["fechar", "salvou"]);

const utils = globalUtils;

const descricaoFormatada = computed(() => formatarDatasNoTexto(props.posvenda?.descricao));

function formatarDatasNoTexto(texto) {
  if (!texto) return "";

  // troca qualquer YYYY-MM-DD por DD/MM/YYYY dentro do texto
  return texto.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, yyyy, mm, dd) => {
    return `${dd}/${mm}/${yyyy}`;
  });
}


// Dados do formulário do modal
const form = reactive({
  // Aqui o modal não tem o campo data_contato, pois vamos atualizar apenas a descrição, próximo contato, etc.
  descricao: "",
  proximo_contato: ""
});

// Histórico de pós-vendas para o registro atual
const historico = ref([]);

// Dados do aluno e responsável (informações da tabela pessoas_fisicas)
const alunoInfo = reactive({ nome: "", celular: "", email: "" });
const responsavelInfo = reactive({ nome: "", celular: "", email: "" });

// Validação: a descrição é obrigatória
const formIsValid = computed(() => form.descricao);

// Função para fechar o modal
function fecharModal() {
  emit("fechar");
}


// Carrega o histórico de posvendas para o registro atual
async function carregarHistorico() {
  if (props.posvenda && props.posvenda.id) {
    try {
      const response = await axios.get("/operacional/posvendas/posvendas_historico", {
        params: { posvenda_id: props.posvenda.id }
      });
      historico.value = response.data;
    } catch (error) {
      console.error("Erro ao carregar histórico de pós-venda:", error);
    }
  }
}

// Carrega as informações do aluno e do responsável, consultando o endpoint de pessoas-fisicas
async function carregarPessoas() {
  // Carrega dados do aluno, usando aluno_id da posvenda
  if (props.posvenda.aluno_id) {
    try {
      const response = await axios.get(`/cadastros/pessoas-fisicas/${props.posvenda.aluno_id}`);
      Object.assign(alunoInfo, {
        nome: response.data.nome,
        celular: response.data.celular,
        email: response.data.email
      });
    } catch (error) {
      console.error("Erro ao carregar dados do aluno:", error);
    }
  }
  // Carrega dados do responsável, usando responsavel_id da posvenda
  if (props.posvenda.responsavel_id) {
    try {
      const response = await axios.get(`/cadastros/pessoas-fisicas/${props.posvenda.responsavel_id}`);
      Object.assign(responsavelInfo, {
        nome: response.data.nome,
        celular: response.data.celular,
        email: response.data.email
      });
    } catch (error) {
      console.error("Erro ao carregar dados do responsável:", error);
    }
  }
}

// Função para salvar:
// 1. Inserir registro no histórico com os dados atuais da posvenda (antes da atualização)
// 2. Atualizar o registro na tabela posvendas com os novos valores
async function salvar() {
  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    const userName = instance?.appContext.config.globalProperties.$globalState.userName || '';

    // 1. Inserir no histórico utilizando os dados atuais da posvenda
    const historicoPayload = {
      posvenda_id: props.posvenda.id,
      nome_usuario: props.posvenda.nome_usuario, // dados atuais da posvenda
      data_contato: props.posvenda.data_contato,
      descricao: props.posvenda.descricao,
      proximo_contato: props.posvenda.proximo_contato
    };
    await axios.post("/operacional/posvendas/posvendas_historico", historicoPayload);

    // 2. Atualizar a posvenda com os dados do modal
    // Se o campo "proximo_contato" não for preenchido, finalizado deverá ser true; caso contrário, false.
    const finalizado = form.proximo_contato ? false : true;
    const posvendaUpdatePayload = {
      matricula_id: props.posvenda.matricula_id, // valor já existente
      data_contato: props.posvenda.data_contato,  // mantemos os dados anteriores, se necessário
      descricao: form.descricao,
      proximo_contato: form.proximo_contato || null,
      nome_usuario: userName,
      aluno_id: props.posvenda.aluno_id, // novos campos
      responsavel_id: props.posvenda.responsavel_id,
      finalizado
    };
    await axios.put(`/operacional/posvendas/${props.posvenda.id}`, posvendaUpdatePayload);

    emit("salvou", { posvendaId: props.posvenda.id });
    fecharModal();
  } catch (error) {
    console.error("Erro ao realizar pós-venda:", error);
  } finally {
    isSubmitting.value = false;
  }
}

// Sempre que o modal for aberto, carregamos o histórico e as informações de pessoas
watch(
  () => props.mostrar,
  async (newVal) => {
    if (newVal) {
      // Reseta o formulário do modal e ativa o loading
      form.descricao = "";
      form.proximo_contato = "";
      isLoading.value = true;
      
      // Aguarda o carregamento dos dados
      await Promise.all([
        carregarHistorico(),
        carregarPessoas()
      ]);
      
      isLoading.value = false;
    }
  }
);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 700px;
  margin: 0 10px;
  border-radius: 4px;
  overflow-y: auto; /* ativa a rolagem vertical se necessário */
  max-height: 90vh; /* altura máxima de 90% da altura da viewport */
}

.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-content {
  padding: 15px;
}

label {
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 0px;
}

input[type="date"] {
  max-width: 220px;
}

/* Remove o negrito dos cabeçalhos da tabela */
.table thead th {
  font-weight: normal;
}
</style>
